import React, { useState } from "react";

/**
 * Inline replacement for window.confirm() / window.prompt().
 * Renders a trigger; clicking it expands in-place into a confirm bar
 * (optionally with a reason text input) instead of a browser dialog.
 *
 * Props:
 *   triggerLabel      — content of the trigger button
 *   triggerClassName
 *   message           — optional line shown above the confirm/cancel buttons
 *   confirmLabel      — defaults to "Confirm"
 *   confirmClassName
 *   requireReason     — if true, shows a text input; onConfirm receives (reason)
 *   reasonOptional    — if true (with requireReason), empty reason is allowed. Default true.
 *   reasonPlaceholder
 *   onConfirm         — (reason?) => void | Promise<void>
 */
export const InlineConfirm = ({
  triggerLabel,
  triggerClassName,
  message,
  confirmLabel = "Confirm",
  confirmClassName = "px-3 py-1.5 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg transition",
  requireReason = false,
  reasonOptional = true,
  reasonPlaceholder = "Reason (optional)",
  onConfirm,
}) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const cancel = () => {
    setOpen(false);
    setReason("");
  };

  const confirm = async () => {
    if (requireReason && !reasonOptional && !reason.trim()) return;
    setBusy(true);
    try {
      await onConfirm(requireReason ? reason.trim() : undefined);
      cancel();
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={triggerClassName}>
        {triggerLabel}
      </button>
    );
  }

  return (
    <div className="inline-flex flex-col gap-2 border dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/50 max-w-xs">
      {message && (
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
          {message}
        </p>
      )}
      {requireReason && (
        <input
          type="text"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={reasonPlaceholder}
          className="border dark:border-gray-500 p-2 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      )}
      <div className="flex gap-2">
        <button onClick={confirm} disabled={busy} className={confirmClassName}>
          {busy ? "Working…" : confirmLabel}
        </button>
        <button
          onClick={cancel}
          disabled={busy}
          className="px-3 py-1.5 text-sm border dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
